/**
 * Line-level diff via @pierre/diffs. Ported from the #5 prototype (questions 1 and 2).
 *
 * @pierre/theme@2.0.0 must be a DIRECT dependency: @pierre/theming peers ^1.1.0
 * optionally while @pierre/diffs pins 2.0.0, so npm nests it out of reach and the
 * production build fails on `@pierre/theme/pierre-light-tritanopia`. Upstream bug in
 * 1.3.1 -- see the #5 resolution.
 *
 * Diff from the two Policy texts, not a hunked patch. A patch-only render is
 * `isPartial`, so pierre cannot expand omitted lines. `MultiFileDiff` keeps the
 * full files so the in-diff hunk expanders can open unchanged regions.
 */
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { MultiFileDiff } from '@pierre/diffs/react';

interface Props {
  oldText: string;
  newText: string;
  oldLabel: string;
  newLabel: string;
  split: boolean;
  /** @pierre/diffs' own intra-line granularity. 'none' = pure line diff. */
  lineDiffType: 'word' | 'char' | 'none';
}

const withTrailingNewline = (text: string) => (text.endsWith('\n') ? text : `${text}\n`);

/** `.md` extension so Shiki picks Markdown rather than treating rubric prose as unknown. */
export const PierreDiff = ({
  oldText,
  newText,
  oldLabel,
  newLabel,
  split,
  lineDiffType,
}: Props) => {
  const oldFile = useMemo(
    () => ({ name: `${oldLabel}.md`, contents: withTrailingNewline(oldText) }),
    [oldLabel, oldText]
  );
  const newFile = useMemo(
    () => ({ name: `${newLabel}.md`, contents: withTrailingNewline(newText) }),
    [newLabel, newText]
  );

  return (
    <Box sx={{ fontSize: 13 }}>
      <MultiFileDiff
        oldFile={oldFile}
        newFile={newFile}
        options={{
          diffStyle: split ? 'split' : 'unified',
          overflow: 'wrap',
          lineDiffType,
          theme: 'github-light',
          themeType: 'light',
        }}
      />
    </Box>
  );
};
